import { Button, buttonVariants } from '../components/ui/button';
import { FaArrowLeft, FaArrowRight, FaEye, FaStepForward } from 'react-icons/fa';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import catalog from './catalog.json';
import OpeningBoard, { linePosition } from './OpeningBoard';
import OpeningFamilyPicker from './OpeningFamilyPicker';
import { OpeningSignIn } from './OpeningLayout';
import { openingRequest, savingAvailable } from './api';
import { useWoodpeckerAuth } from '../woodpecker/Woodpecker';

const families = [...new Set(catalog.lines.map(line => line.family))];
const defaultDraft = { family: families[0], side: 'white', name: '', lineIds: catalog.lines.filter(line => line.family === families[0]).map(line => line.id) };

export default function OpeningLibrary() {
  const auth = useWoodpeckerAuth();
  const { idToken, authProvider, isAuthenticated } = auth;
  const navigate = useNavigate();
  const [draft, setDraft] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem('opening_draft'));
      return saved && families.includes(saved.family) && ['white', 'black'].includes(saved.side) && Array.isArray(saved.lineIds) ? saved : defaultDraft;
    } catch { return defaultDraft; }
  });
  const [search, setSearch] = useState('');
  const [preview, setPreview] = useState(catalog.lines.find(line => line.id === draft.lineIds[0]) || catalog.lines[0]);
  const [moveIndex, setMoveIndex] = useState(0);
  const [sets, setSets] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  useEffect(() => { sessionStorage.setItem('opening_draft', JSON.stringify(draft)); }, [draft]);
  useEffect(() => {
    let active = true;
    setSets([]);
    setCursor(null);
    setLoading(false);
    if (isAuthenticated && savingAvailable) {
      setLoading(true);
      openingRequest('', { idToken, authProvider })
        .then(data => { if (active) { setSets(data.sets); setCursor(data.cursor); setError(''); } })
        .catch(error => { if (active) setError(error.message); })
        .finally(() => { if (active) setLoading(false); });
    }
    return () => { active = false; };
  }, [isAuthenticated, idToken, authProvider, refresh]);

  const familyLines = catalog.lines.filter(line => line.family === draft.family);
  const visibleLines = familyLines.filter(line => line.name.toLowerCase().includes(search.toLowerCase()));
  const changeFamily = family => {
    const lines = catalog.lines.filter(line => line.family === family);
    setDraft({ ...draft, family, lineIds: lines.map(line => line.id) });
    setPreview(lines[0]); setMoveIndex(0); setSearch('');
  };
  const create = async event => {
    event.preventDefault(); setBusy(true); setError('');
    try {
      const { set } = await openingRequest('', auth, 'POST', { ...draft, catalogVersion: catalog.version });
      sessionStorage.removeItem('opening_draft');
      navigate(`/openings/sets/${set.setId}`);
    } catch (error) { setError(error.message); }
    finally { setBusy(false); }
  };
  const remove = async set => {
    if (!window.confirm(`Delete "${set.name}" and its cycle history?`)) return;
    setBusy(true);
    try { await openingRequest(`/${set.setId}`, auth, 'DELETE'); setSets(sets.filter(item => item.setId !== set.setId)); }
    catch (error) { setError(error.message); }
    finally { setBusy(false); }
  };
  const loadMore = async () => {
    setLoading(true);
    try { const data = await openingRequest(`?cursor=${encodeURIComponent(cursor)}`, auth); setSets(previous => [...previous, ...data.sets]); setCursor(data.cursor); }
    catch (error) { setError(error.message); }
    finally { setLoading(false); }
  };

  return <>
    <div className="opening-eyebrow">Woodpecker training for openings</div>
    <h1>Make the opening moves familiar.</h1>
    <p>Choose a side and a few variations. Repeat the same lines in cycles, then focus on your remaining mistakes.</p>
    {error && <div role="alert" className="opening-error">{error} <Button variant="neutral" size="action" className="m-0 border-solid" onClick={() => setRefresh(value => value + 1)}>Retry loading sets</Button></div>}
    {isAuthenticated && <section aria-label="Your opening sets">
      <h2>Your training sets</h2>
      {loading && <p role="status">Loading sets...</p>}
      {!loading && !error && !sets.length && <p className="opening-muted">Create your first opening set below.</p>}
      {sets.length > 0 && <div className="opening-sets">{sets.map(set => <article key={set.setId} className="opening-panel">
        <h2>{set.name}</h2><p>{set.side === 'white' ? 'White' : 'Black'} · {set.lineIds.length} lines · Cycle {set.cycleNumber}</p>
        {set.progress && <p>{set.progress.mode === 'mistakes' ? 'Mistakes only' : 'All lines'}: {set.progress.lineIndex} / {set.progress.lineIds.length} complete</p>}
        <div className="opening-actions"><Link className={buttonVariants({ variant: 'brand', size: 'action', className: 'm-0 border-solid' })} to={`/openings/sets/${set.setId}`}>{set.progress ? 'Resume training' : 'Open set'}</Link><Button variant="neutral" size="action" className="m-0 border-solid" disabled={busy} onClick={() => remove(set)}>Delete</Button></div>
      </article>)}</div>}
      {cursor && <Button variant="neutral" size="action" className="m-0 border-solid" disabled={loading} onClick={loadMore}>Load more sets</Button>}
    </section>}
    <div className="opening-grid opening-creation-grid">
      <form className="opening-panel" onSubmit={create}>
        <h2>Build a training set</h2>
        <OpeningFamilyPicker families={families} value={draft.family} onChange={changeFamily} />
        <fieldset><legend>Playing side</legend>{['white', 'black'].map(side => <label key={side}><input type="radio" name="side" value={side} checked={draft.side === side} onChange={() => setDraft({ ...draft, side })} />{side === 'white' ? 'White' : 'Black'}</label>)}</fieldset>
        <label htmlFor="opening-search">Find a variation</label><input id="opening-search" className="min-h-12 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-base text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-700" value={search} onChange={event => setSearch(event.target.value)} placeholder="Search this opening family" />
        {visibleLines.map(line => <div className="opening-line" key={line.id}>
          <label><input type="checkbox" checked={draft.lineIds.includes(line.id)} onChange={event => setDraft({ ...draft, lineIds: event.target.checked ? [...draft.lineIds, line.id] : draft.lineIds.filter(id => id !== line.id) })} /><span>{line.name}<small>{draft.side === 'white' ? Math.ceil(line.moves.length / 2) : Math.floor(line.moves.length / 2)} moves to play · Sequence only</small></span></label>
          <Button variant="neutral" size="action" className="m-0 w-12 border-solid p-0" type="button" aria-label={`Preview ${line.name}`} title={`Preview ${line.name}`} onClick={() => { setPreview(line); setMoveIndex(0); }}><FaEye aria-hidden="true" /></Button>
        </div>)}
        {!visibleLines.length && <p>No matching variations in this family.</p>}
        <p className="opening-muted">{draft.lineIds.length} lines selected. Each ends at its named opening position.</p>
        <label htmlFor="opening-set-name">Set name</label><input id="opening-set-name" className="min-h-12 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-base text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-700" value={draft.name} maxLength={80} required onChange={event => setDraft({ ...draft, name: event.target.value })} placeholder={`My ${draft.family} lines`} />
        {!savingAvailable && <p className="opening-muted">Cloud saving is not available yet. Try a sample line while it is being connected.</p>}
        <Button variant="brand" size="action" className="m-0 border-solid" disabled={!isAuthenticated || !savingAvailable || busy || !draft.lineIds.length}>{busy ? 'Saving...' : 'Create training set'}</Button>
        {!isAuthenticated && <p className="opening-muted">Sign in below to save a set. Your selection will be kept.</p>}
      </form>
      <section className="opening-panel">
        <div className="opening-eyebrow">Line preview</div><h2>{preview.name}</h2>
        <OpeningBoard game={linePosition(preview, moveIndex)} side={draft.side} />
        <div className="opening-actions"><Button variant="neutral" size="action" className="m-0 w-12 border-solid p-0" aria-label="Previous preview move" title="Previous preview move" disabled={!moveIndex} onClick={() => setMoveIndex(value => value - 1)}><FaArrowLeft aria-hidden="true" /></Button><span>{moveIndex} / {preview.moves.length} half-moves</span><Button variant="neutral" size="action" className="m-0 w-12 border-solid p-0" aria-label="Next preview move" title="Next preview move" disabled={moveIndex === preview.moves.length} onClick={() => setMoveIndex(value => value + 1)}><FaArrowRight aria-hidden="true" /></Button><Button variant="neutral" size="action" className="m-0 w-12 border-solid p-0" aria-label="Target position" title="Target position" onClick={() => setMoveIndex(preview.moves.length)}><FaStepForward aria-hidden="true" /></Button></div>
        <p className="opening-moves">{preview.pgn}</p>
        <Link className={buttonVariants({ variant: 'brand', size: 'action', className: 'm-0 border-solid' })} to={`/openings/sample/${preview.id}?side=${draft.side}`}>Try this line</Link>
      </section>
    </div>
    <OpeningSignIn />
  </>;
}
